import axios from 'axios';
import querystring from 'querystring';
import * as _ from 'lodash';
import Cookies from 'js-cookie';
import { sessionService } from 'redux-react-session';
import {
    DOMAIN_COOKIE_NAME,
    getCookie,
    DAARA_COOKIE_NAME,
    DAARA_TOKEN_PREFIX,
    getAccountsDashboardUrl,
    extractFilenameFromContentDisposition
} from './utilityService';
import features from './daara-config.json';
import { appConfig } from '../config/appConfig';

export const getAPIbaseUrl = () => process.env.REACT_APP_BASE_API_URL;

const BASE_URL_RELIANCE = getAPIbaseUrl();

export const getRedirectDomain = (redirectDomain) => {
    return `${redirectDomain}.${process.env.REACT_APP_DOMAIN_NAME}`;
};

const getDomain = () => process.env.REACT_APP_DOMAIN_NAME;

export const DOMAIN = getDomain();

class ClientService {
    constructor() {
        this.ax = axios.create({
            baseURL: BASE_URL_RELIANCE,
            headers: {
                DAARA_TOKEN: getCookie(DAARA_COOKIE_NAME)
            }
        });
        this.data = {};
    }

    async refreshDaaraToken() {
        try {
            // Get refresh token from cookies (client-accessible)
            const refreshToken = getCookie('DAARA_REFRESH_TOKEN');

            if (!refreshToken) {
                throw new Error('Refresh token not found in cookies');
            }

            // Make refresh request with explicit Authorization header
            const response = await axios.get(
                `${appConfig.daaraServiceBaseUrl}/accountservice/api/refresh-token`,
                {
                    headers: {
                        Authorization: refreshToken
                    }
                }
            );

            const { access_token, refresh_token } = response.data;

            // Update localStorage with new access token
            localStorage.setItem(`${DAARA_COOKIE_NAME}_${DAARA_TOKEN_PREFIX}`, access_token);

            // Update DAARA cookie with new access token (client-accessible)
            Cookies.set(DAARA_COOKIE_NAME, access_token, { domain: getDomain() });

            // Update DAARA_REFRESH_TOKEN cookie with new refresh token (client-accessible)
            if (refresh_token) {
                Cookies.set(`${DAARA_COOKIE_NAME}_REFRESH_TOKEN`, refresh_token, { domain: getDomain() });
            }

            // Update Redux session with new tokens
            const user = await sessionService.loadUser();
            user.credentials.daaraToken = access_token;
            if (refresh_token) {
                user.credentials.refreshToken = refresh_token;
            }
            await sessionService.saveUser(user);

            return access_token;
        } catch (error) {
            console.error('Token refresh failed:', error);
            // Clear tokens on refresh failure
            localStorage.removeItem(`${DAARA_COOKIE_NAME}_${DAARA_TOKEN_PREFIX}`);

            // Redirect to accounts dashboard login
            const returnUrl = encodeURIComponent(window.location.href);
            window.location.href = `${getAccountsDashboardUrl()}/login?r=${returnUrl}`;

            throw new Error('Token refresh failed: ' + error.message);
        }
    }

    auth() {
        let accessToken = getCookie(DOMAIN_COOKIE_NAME);
        let daaraToken = getCookie(DAARA_COOKIE_NAME);
        return this.ax
            .post(`/auth/clients`, null, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                let client = mapClient(response.data.data.accounts);
                client.credentials.accessToken = accessToken;
                client.credentials.daaraToken = daaraToken;
                if (features.account_service.auth) {
                    localStorage.setItem(
                        `${DAARA_COOKIE_NAME}_${DAARA_TOKEN_PREFIX}`,
                        client.credentials.daaraToken
                    );
                }
                client._lastValidatedAt = Date.now();
                return client;
            });
    }

    getUsageData(accessToken, numberOfDays, clientId) {
        this.data = {
            num_of_days: numberOfDays
        };
        return this.ax
            .get(
                `/clients/${clientId}/usage`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                },
                this.data
            )
            .then((response) => {
                return response.data.data;
            });
    }

    getInvoices(accessToken, clientId) {
        return this.ax
            .get(`/clients/${clientId}/invoices`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    changeLogo(logo) {
        const formData = new FormData();
        formData.append('file_use', 'client_logo');
        formData.append('file', logo);
        return this.ax
            .post(`/upload`, formData, {
                header: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    updateProfile(email, phone, logo_filename, accessToken, client_id) {
        this.data = querystring.stringify({
            email_address: email,
            phone_number: phone,
            logo_filename
        });
        return this.ax
            .patch(`/clients/${client_id}/profile`, this.data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.message;
            })
            .catch((error) => {
                // Log or handle error depending on your use case
                if (error.response) {
                    // Server responded with a status code outside 2xx
                    console.error('Server Error:', error.response.status, error.response.data);
                    throw new Error(error.response.data?.message || 'Failed to update profile');
                } else if (error.request) {
                    // Request was made but no response received
                    console.error('Network Error:', error.request);
                    throw new Error('No response from server. Please check your connection.');
                } else {
                    // Something went wrong setting up the request
                    console.error('Request Setup Error:', error.message);
                    throw new Error('Unexpected error occurred while updating profile.');
                }
            });
    }

    getStaff(accessToken, clientId, queryData) {
        if (queryData.invoice_ref_code === '') delete queryData.invoice_ref_code;
        return this.ax
            .get(`/clients/${clientId}/enrollees?${querystring.stringify(queryData)}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data;
            });
    }

    getPolicies(accessToken, clientId, queryData) {
        return this.ax
            .get(`clients/${clientId}/policy?${querystring.stringify({ client_id: clientId, ...queryData })}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    getPolicyCertificate(accessToken, clientId, policyId) {
        return this.ax
            .get(`clients/${clientId}/policies/${policyId}/certificate`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                responseType: 'blob'
            })
            .then((response) => ({
                blob: response.data,
                filename: extractFilenameFromContentDisposition(response.headers['content-disposition'])
            }));
    }

    getInvoiceByReferenceCode(refCode, accessToken, clientId) {
        return this.ax
            .get(`/clients/${clientId}/invoices/${refCode}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    getInvoicePlanMasterList(refCode, accessToken, clientId) {
        return this.ax
            .get(`/clients/${clientId}/invoices/${refCode}/list-download`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    getAllPlans() {
        return this.ax.get(`/products?source=smewebsignup`).then((response) => {
            return response.data.data;
        });
    }

    purchasePlan(orderDetails, access_token) {
        return this.ax
            .post(`/cart`, orderDetails, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${access_token}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    getClientCards(client_id, access_token) {
        return this.ax
            .get(`/clients/${client_id}/cards`, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    removeClientCard(client_id, access_token, cardId) {
        return this.ax
            .delete(`/clients/${client_id}/cards/${cardId}`, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    newInvoice(invoice, access_token) {
        invoice.callback_url = '/payment-successful';
        return this.ax
            .post(`/cart`, invoice, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${access_token}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    addSingleEmployee(
        firstName,
        lastName,
        email,
        phone,
        productId,
        numOfDependants,
        accessToken,
        clientId,
        invoiceRefCode
    ) {
        this.data = {
            first_name: firstName,
            last_name: lastName,
            email_address: email,
            phone_number: phone,
            product_id: productId,
            number_of_dependants: numOfDependants || 0
        };

        return this.ax
            .post(`/clients/${clientId}/invoices/${invoiceRefCode}/enrollees`, this.data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    addMultipleEmployees(formData, accessToken, clientId, refCode) {
        return this.ax
            .post(`/clients/${clientId}/invoices/${refCode}/list-upload`, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }
    addMultipleEmployees(formData, accessToken, clientId, refCode) {
        return this.ax
            .post(`/clients/${clientId}/invoices/${refCode}/list-upload`, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response;
            });
    }
    getClientAdminAccountById(accessToken, clientId) {
        return this.ax
            .get(`/clients/${clientId}/profile`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    getStaffById(id, accessToken, clientId) {
        return this.ax
            .get(`/clients/${clientId}/enrollees/${id}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.data;
            });
    }

    editEmployee(userId, firstName, lastName, email, phone, accessToken, clientId) {
        this.data = querystring.stringify({
            first_name: firstName,
            last_name: lastName,
            email_address: email,
            phone_number: phone
        });

        return this.ax
            .patch(`/clients/${clientId}/enrollees/${userId}`, this.data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.message;
            });
    }

    removeEnrollee(userId, accessToken, clientId) {
        return this.ax
            .delete(`/clients/${clientId}/enrollees/${userId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then((response) => {
                return response.data.message;
            });
    }

    changePaymentFrequency(accessToken, clientId, invoiceRefCode, frequency) {
        return this.ax
            .put(
                `/clients/${clientId}/invoices/${invoiceRefCode}?${querystring.stringify({
                    frequency
                })}`,
                null,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            )
            .then((response) => {
                return response.data.message;
            });
    }

    getOverViewData(accessToken, clientId) {
        return this.ax
            .get(`/clients/${clientId}/utilizationUrl`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then((response) => {
                return response.data.data;
            });
    }

    getClientAdmins(accessToken, clientId) {
    return this.ax
        .get(`/clients/${clientId}/admins`, {
            headers: { Authorization: `Bearer ${accessToken}` }
        })
        .then((response) => response.data.data.admin_emails);
}
}

export function mapClient(accounts) {
    return {
        companyInfo: {
            id: _.get(accounts[0], 'id'),
            name: _.get(accounts[0], 'name'),
            email: _.get(accounts[0], 'email_address'),
            phoneNumber: _.get(accounts[0], 'phone_number'),
            address: _.get(accounts[0], 'address'),
            logoUrl: _.get(accounts[0], 'logo'),
            industry: _.get(accounts[0], 'industry'),
            canModify: _.get(accounts[0], 'can_modify'),
            type: _.get(accounts[0], 'type')
        },
        adminInfo: {
            position: _.get(accounts[0], 'position'),
            email: _.get(accounts[0], 'admin_email_address'),
            firstName: _.get(accounts[0], 'admin_first_name'),
            lastName: _.get(accounts[0], 'admin_last_name'),
            accounts: accounts
        },
        credentials: {
            accessToken: _.get(accounts[0], 'access_token'),
            daaraToken: _.get(accounts[0], 'daara_token'),
            refreshToken: _.get(accounts[0], 'daara_refresh_token')
        },
        preferences: {
            allowNotifications: '',
            selectedClient: accounts.length > 1 ? -1 : _.get(accounts[0], 'id'), //if client admin has multiple accounts, set selectedClient ot -1 so that they select which client they want,
            clientShortCode: _.get(accounts[0], 'short_code')
        }
    };
}

// Merge a freshly fetched user (`fresh`) over a cached user (`cached`) during
// stale-while-revalidate (EN-583). `fresh` is authoritative for
// accounts/adminInfo/companyInfo/credentials; we only carry the user's
// previously selected company forward so multi-account admins aren't bounced
// back to /select-account. Operates on `fresh`; never mutates `cached`.
export function reconcileUser(cached, fresh) {
    const selected = cached && cached.preferences && cached.preferences.selectedClient;

    // Single account, or no explicit selection (-1/absent) -> fresh wins as-is.
    if (!selected || selected === -1) {
        return fresh;
    }

    const accounts = _.get(fresh, 'adminInfo.accounts', []);
    const match = accounts.find((account) => account.id === selected);

    // Selected company no longer present in the fresh accounts -> fall back to fresh.
    if (!match) {
        return fresh;
    }

    // Mirror AccountSelectionComponent.switchClients: rebuild companyInfo from the
    // selected account and preserve the selection + its short code.
    fresh.companyInfo = mapClient([match]).companyInfo;
    fresh.preferences.selectedClient = selected;
    fresh.preferences.clientShortCode = match.short_code;

    return fresh;
}

export default new ClientService();
